// eslint-disable-next-line no-unused-vars
import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import ServerStylesConditionalWrapper from '../../shared/ServerStylesConditionalWrapper';
import useMediaQuery from '../../custom-hooks/useMediaQuery';
import { device } from '../../../utils/styled-component-mixins';
import Accordion from '../../shared/Accordion';
import { useLocalization } from '../../custom-hooks/useLocalization';
import { withLocalization } from '../../shared/Context/LocalizationContext';
import ServiceLandingTabContent from './ServiceLandingTabContent';

const TabsWrapperStyled = styled.div`
  [role='tablist'] {
    display: flex;
    justify-content: center;
    overflow: visible;
    width: 100%;
  }

  [role='tab'] {
    border: 0;
    position: relative;
    margin: 0;
    padding: 1.6rem 4rem;
    overflow: visible;
    font-size: 2.2rem;
    font-weight: 800;
    line-height: 3.2rem;
  }

  [role='tab'][aria-selected='true'] {
    border-radius: 0;
    background-color: var(--slate-transparent);
  }

  [role='tab']:hover,
  [role='tab']:focus,
  [role='tab']:active {
    border-radius: 0;
    color: inherit;
  }

  .insight-card {
    box-shadow: 0px 30px 80px rgb(91 110 127 / 25%);
  }

  [role='tabpanel'] {
    padding: 0 0 6.7rem 0;
    position: relative;
    z-index: 2;
  }

  [role='tabpanel'].is-hidden {
    display: none;
  }

  .tab-wysiwyg {
    line-height: 2.4rem;
  }

  .intro-content {
    margin-bottom: 5rem;
  }

  .tabpanel-wrapper {
    margin-bottom: 7.5rem;

    @media ${device.desktop} {
      margin-bottom: 2.4rem;
    }
  }
`;

const ServiceLandingTabs = (props) => {
  const [activeTab, setActiveTab] = useState('');
  const { items } = props;
  const tabListRef = useRef(null);
  const isDesktop = useMediaQuery(device.desktop);
  const { resourceStrings } = useLocalization();
  const linkText = resourceStrings['bdO.FeaturedInsight.LinkText'];
  const tabModalActivationText = resourceStrings['bdO.ServiceLanding.ExploreServiceAreaText'];
  const filterLabelText = resourceStrings['bdO.ServiceLanding.FilterLabelText'];
  const modalCtaText = resourceStrings['bdO.ServiceLanding.ModalCTAText'];
  const modalListExpand = resourceStrings['bdO.ServiceLanding.ModalListExpand'];
  const modalListCollapse = resourceStrings['bdO.ServiceLanding.ModalListCollapse'];

  const deactivateTabs = () => {
    const tabs = document.querySelectorAll('[role="tab"]');
    tabs.forEach((tab) => {
      tab.setAttribute('aria-selected', false);
    });

    const tabPanels = document.querySelectorAll('[role="tabpanel"]');
    tabPanels.forEach((tabPanel) => {
      tabPanel.classList.add('is-hidden');
    });
  };

  const activateTab = (tab, pageTitle) => {
    deactivateTabs();

    tab.setAttribute('aria-selected', true);
    const controls = tab.getAttribute('aria-controls');
    setActiveTab(pageTitle);

    document.getElementById(controls).classList.remove('is-hidden');
  };

  const initActiveTab = (e) => {
    const url = window.location.hash;
    const uri = url?.split('#')[1];
    const tabs = document.querySelectorAll('[role="tab"]');
    const headerHeight = document.querySelector('header').offsetHeight;
    const localNavHeight = document.querySelector('.local-nav')?.offsetHeight;

    window.history.scrollRestoration = 'manual';
    let tabMatchesUri = false;
    tabs?.forEach((tab) => {
      const tabId = tab.getAttribute('id');
      if (tabId?.toLowerCase() === uri) {
        tabMatchesUri = true;
        activateTab(tab);

        if (e.type === 'load') {
          window.scroll(0, tab.offsetTop - headerHeight - localNavHeight);
        }
      }
    });

    if (!tabMatchesUri) {
      activateTab(tabs[0], tabs[0].pageTitle);
    }
  };

  useEffect(() => {
    window.addEventListener('load', initActiveTab);
    window.addEventListener('hashchange', initActiveTab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <ServerStylesConditionalWrapper>
      <div ref={tabListRef}>
        {isDesktop && (
          <TabsWrapperStyled>
            <div role="tablist" aria-label="tabs">
              {items.map((item, index) => (
                <button
                  key={`${item.pageTitle}-${index}`}
                  type="button"
                  role="tab"
                  aria-controls={`${item.pageTitle}-tab`}
                  id={item.pageTitle}
                  onClick={(event) => activateTab(event.currentTarget, event.target.innerText)}
                  aria-selected={`${index === 0 ? 'true' : 'false'}`}
                >
                  {item.pageTitle}
                </button>
              ))}
            </div>
            <div className="tabpanel-wrapper">
              {items.map((item, index) => (
                <ServiceLandingTabContent
                  key={item.pageTitle}
                  item={item}
                  index={index}
                  linkText={linkText}
                  tabModalActivationText={tabModalActivationText}
                  filterLabelText={filterLabelText}
                  activeTab={activeTab}
                  modalCtaText={modalCtaText}
                />
              ))}
            </div>
          </TabsWrapperStyled>
        )}
        {!isDesktop && (
          <div className="service-landing-accordions">
            {items.map((item, index) => (
              <Accordion
                key={`${item.pageTitle}`}
                {...item}
                index={index}
                contentPadding="0 0 0 0"
                className="accordion-item"
              >
                <ServiceLandingTabContent
                  item={item}
                  serviceAreaIndex={index}
                  linkText={linkText}
                  tabModalActivationText={tabModalActivationText}
                  filterLabelText={filterLabelText}
                  activeTab={activeTab}
                  modalCtaText={modalCtaText}
                  modalListExpand={modalListExpand}
                  modalListCollapse={modalListCollapse}
                />
              </Accordion>
            ))}
          </div>
        )}
      </div>
    </ServerStylesConditionalWrapper>
  );
};

export default withLocalization(ServiceLandingTabs);
